import React from "react"

const formatNodeForContext = (
  {
    locationName,
    name,
    geographicScale,
    districtType,
    homeCareSpecialisms,
    dementiaCareHomeCount,
    nursingCareHomeCount,
    residentialCareHomeCount,
    palliativeCareHomeCount,
    homeCareCount,
    careHomeCount,
    ciCareHomeRatedPositivelyCount,
    ciHomeCareRatedPositivelyCount,
    cqcCareHomeRatedPositivelyCount,
    cqcHomeCareRatedPositivelyCount,
    featuredCareHomeTowns,
    featuredHomeCareTowns,
    featuredDementiaTowns,
    featuredNursingTowns,
    featuredPalliativeTowns,
    featuredResidentialTowns,
  },
  { totalBranchRecordCount, regulatoryBody },
) => ({
  name: locationName || name,
  locationName: locationName || name,
  geographicScale: geographicScale || null,
  districtType: districtType || null,
  regulatoryBody: regulatoryBody || null,
  homeCareSpecialisms: homeCareSpecialisms || null,
  dementiaCareHomeCount,
  nursingCareHomeCount,
  palliativeCareHomeCount,
  residentialCareHomeCount,
  totalBranchRecordCount: totalBranchRecordCount || null,
  totalHomeCareCount: homeCareCount,
  totalCareHomeCount: careHomeCount,
  ciCareHomeRatedPositivelyCount,
  ciHomeCareRatedPositivelyCount,
  cqcCareHomeRatedPositivelyCount,
  cqcHomeCareRatedPositivelyCount,
  careHomeRatedPositivelyCount: {
    ci: ciCareHomeRatedPositivelyCount,
    cqc: cqcCareHomeRatedPositivelyCount,
  }[regulatoryBody],
  homeCareRatedPositivelyCount: {
    ci: ciHomeCareRatedPositivelyCount,
    cqc: cqcHomeCareRatedPositivelyCount,
  }[regulatoryBody],
  featuredCareHomeTowns: Array.isArray(featuredCareHomeTowns)
    ? featuredCareHomeTowns
    : [],
  featuredHomeCareTowns: Array.isArray(featuredHomeCareTowns)
    ? featuredHomeCareTowns
    : [],
  featuredDementiaTowns,
  featuredNursingTowns,
  featuredPalliativeTowns,
  featuredResidentialTowns,
})

export const getSingleGeoContextData = (
  geoPathData,
  {
    parentGeoPathData,
    siblingGeoPathDatas,
    totalBranchRecordCount,
    regulatoryBody,
  } = {},
) => {
  const baseNode = formatNodeForContext(geoPathData, {
    totalBranchRecordCount,
    regulatoryBody,
  })

  const parent = parentGeoPathData
    ? formatNodeForContext(parentGeoPathData, {})
    : null
  const siblings = siblingGeoPathDatas
    ? siblingGeoPathDatas.map((s) => formatNodeForContext(s, {}))
    : null
  return {
    ...baseNode,
    parent,
    siblings,
  }
}

/**
 * The format of this context is:
 *
 * {
 *   name: <string> - eg 'Coventry'
 *   locationName: <string> - identical to .name
 *   geographicScale: <string> - one of those defined in geographical_scales.js
 *   districtType: <string / null> - null unless geographicScale is DISTRICT_SCALE
 *   regulatoryBody: <string / null> - 'cqc' / 'ci'
 *   homeCareSpecialisms: <object / null> - an object containing key/value pairs       of cqc specialism and count - applicable only to cqc.
 *   dementiaCareHomeCount: <integer / null> - 10,
 *   nursingCareHomeCount: <integer / null> - 344,
 *   palliativeCareHomeCount: <integer / null> - 35,
 *   residentialCareHomeCount: <integer / null> - 35,
 *   totalBranchRecordCount: <integer / null> - how many branch records in the place
 *   totalHomeCareCount: <integer / null> - how many home care branch records in the place
 *   totalCareHomeCount: <integer / null> - how many care home branch records in the place
 *   ciCareHomeRatedPositivelyCount: <integer> - how many CI care homes that are rated good and above
 *   ciHomeCareRatedPositivelyCount: <integer> - how many CI home care agencies that are rated good and above
 *   cqcCareHomeRatedPositivelyCount: <integer> - how many CQC care homes that are rated good and above
 *   cqcHomeCareRatedPositivelyCount: <integer> - how many CQC care home agencies that are rated good and above
 *   homeCareRatedPositivelyCount: <integer> - proxy for ci or cqc value, dependent on regulatory body
 *   careHomeRatedPositivelyCount: <integer> - proxy for ci or cqc value, dependent on regulatory body
 *   featuredCareHomeTowns: <array / null > - an array containing some town data
 *   featuredHomeCareTowns: <array / null > - an array containing some town data
 *   featuredDementiaTowns: <array / null > - an array containing some town data
 *   featuredNursingTowns: <array / null > - an array containing some town data
 *   featuredPalliativeTowns: <array / null > - an array containing some town data
 *   featuredResidentialTowns: <array / null > - an array containing some town data
 *
 *   parent: a single object of the same form as above, but the parent geo of this one
 *   siblings: a list of the same, for all the sibling geos of this one
 * }
 */

const SingleGeoContext = React.createContext(null)
export default SingleGeoContext
